<template>
  <span
    :id="id"
    ref="spanRef"
    :tabindex="focusable ? '0' : '-1'"
    class="inline-flex items-center gap-1 focus:outline-none"
    :class="[
      badgeClass,
      textColorClass,
      (tooltip || clickable) && hoverBgColorClass,
      clickable && 'cursor-pointer',
      square ? 'rounded-md' : 'rounded-full',
    ]"
    @click="onClick"
  >
    <div v-if="dot || icon" class="w-3" :class="{ '-ml-0.5': modelValue }">
      <svg
        v-if="dot"
        :class="[!dotColor && dotColorClass]"
        :style="{
          color: `${dotColor}`,
        }"
        class="mt-0.5 h-2 w-2"
        fill="currentColor"
        viewBox="0 0 8 8"
      >
        <circle cx="4" cy="4" r="3" />
      </svg>
      <component :is="icon" :class="[dotColorClass, modelValue ? '-ml-0.5' : '']" class="h-3 w-3" aria-hidden="true" />
    </div>
    <slot name="icon" />
    <slot> {{ modelValue }} </slot>
    <button
      v-if="deletable"
      tabindex="-1"
      class="inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full focus:outline-none"
      :class="[crossStyleClass, crossClasss]"
      @click.stop="onDelete"
    >
      <svg class="h-2 w-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
        <path stroke-linecap="round" stroke-width="1.5" d="M1 1l6 6m0-6L1 7" />
      </svg>
    </button>
  </span>
</template>

<script setup lang="ts">
import { computed, ref, toRef } from "vue";

//Utils
import { useTooltip } from "@composables/tooltips";

//Types
import type { Component, RenderFunction } from "vue";
import type { Placement } from "@popperjs/core";
import type { Theme, Size } from "./simpleBadge.types";

export interface Props {
  id?: string;
  modelValue?: string;
  theme?: Theme;
  size?: Size;
  square?: boolean;
  deletable?: boolean;
  clickable?: boolean;
  dot?: boolean;
  dotColor?: HexColor;
  icon?: RenderFunction | Component;
  tooltip?: string | ((el: HTMLDivElement) => string);
  tooltipPlacement?: Placement;
  focusable?: boolean;
}

// Props
const props = withDefaults(defineProps<Props>(), {
  id: undefined,
  modelValue: "",
  theme: "sky",
  size: "small",
  square: false,
  dot: false,
  dotColor: undefined,
  deletable: false,
  clickable: false,
  icon: undefined,
  tooltip: "",
  tooltipPlacement: "bottom",
  focusable: false,
});

// Emits
const emit = defineEmits<{
  (e: "delete"): void;
  (e: "click"): void;
}>();

const onDelete = () => {
  emit("delete");
};

const onClick = () => {
  if (!props.clickable) return;
  emit("click");
};

const spanRef = ref();
const contentToRef = toRef(props, "tooltip");
contentToRef.value && useTooltip(spanRef, contentToRef, { placement: props.tooltipPlacement });

// Styles
const paddings: Record<Size, string> = {
  small: `px-2.5 py-0.5 text-xs`,
  large: `px-3 py-0.5 text-sm`,
  xlarge: `px-3 py-1 text-sm`,
};
const deletableClasses: Record<Size, string> = {
  small: "pr-0.5",
  large: "pr-1",
  xlarge: "pr-1",
};

const themes: Record<Theme, string> = {
  green: "bg-success-99",
  "light-green": "bg-success-99",
  yellow: "bg-warning-95",
  blue: "bg-blue-100",
  red: "bg-danger-99",
  sky: "bg-primary-99",
  "sky-solid": "bg-primary-60",
  "light-sky": "bg-primary-99",
  gray: "bg-neutral-99",
  "gray-secondary": "bg-[#f5f5f5]",
  white: "bg-white border focus:border-neutral-90 hover:border-neutral-90",
  "black-alter": "border border-[#737373]",
  "neutral-border": " border border-neutral-80 bg-neutral-99",
};

const defaultClasses = "inline-flex items-center font-medium";

const textColors: Record<Theme, string> = {
  green: "text-success-5",
  "light-green": "text-success-20",
  yellow: "text-warning-20",
  blue: "text-blue-800",
  red: "text-danger-20",
  sky: "text-primary-30",
  "sky-solid": "text-neutral-100",
  "light-sky": "text-primary-60",
  gray: "text-neutral-0",
  "gray-secondary": "text-neutral-40",
  white: "!text-neutral-0",
  "black-alter": "text-neutral-100",
  "neutral-border": "text-neutral-0",
};

const bgColors: Record<Theme, string> = {
  green: "hover:bg-success-99",
  "light-green": "hover:bg-success-99",
  yellow: "hover:bg-warning-99",
  blue: "hover:bg-blue-200",
  red: "hover:bg-danger-99",
  sky: "hover:bg-primary-99",
  "sky-solid": "hover:bg-primary-99",
  "light-sky": "hover:bg-primary-99",
  gray: "hover:bg-neutral-99",
  "gray-secondary": "hover:bg-neutral-99",
  white: "hover:bg-neutral-99",
  "black-alter": "hover:border-[#a3a3a3]",
  "neutral-border": "hover:bg-neutral-99",
};

const dotColors: Record<Theme, string> = {
  green: "text-success-60",
  "light-green": "text-success-60",
  yellow: "text-warning-60",
  blue: "text-blue-400",
  red: "text-danger-60",
  sky: "text-primary-60",
  "sky-solid": "text-neutral-100",
  "light-sky": "text-primary-60",
  gray: "text-neutral-40",
  "gray-secondary": "text-neutral-70",
  white: "!text-neutral-0",
  "black-alter": "text-neutral-100",
  "neutral-border": "text-neutral-0",
};

const crossStyles: Record<Theme, string> = {
  green: "text-success-60 hover:bg-success-20",
  "light-green": "text-success-60 hover:bg-success-20",
  yellow: "text-warning-60 hover:bg-warning-20",
  blue: "text-blue-400 hover:bg-blue-200",
  red: "text-danger-60 hover:bg-danger-20",
  sky: "text-primary-60 hover:bg-primary-20",
  "sky-solid": "text-neutral-100 hover:bg-primary-60",
  "light-sky": "text-primary-60 hover:bg-primary-20",
  gray: "text-neutral-60 hover:bg-neutral-20",
  "gray-secondary": "text-neutral-60 hover:bg-neutral-20",
  white: "!text-neutral-0 hover:bg-neutral-5",
  "black-alter": "text-neutral-100",
  "neutral-border": "text-neutral-0 hover:bg-neutral-99",
};

const crossClasses: Record<Size, string> = {
  small: "",
  large: "",
  xlarge: "mt-0.5",
};

const badgeClass = computed(() => {
  const classes: Array<string> = [defaultClasses];

  classes.push(paddings[props.size]);

  props.deletable && classes.push(deletableClasses[props.size]);

  classes.push(themes[props.theme]);

  return classes.join(" ");
});

const textColorClass = computed(() => textColors[props.theme]);

const hoverBgColorClass = computed(() => bgColors[props.theme]);

const dotColorClass = computed(() => dotColors[props.theme]);

const crossStyleClass = computed(() => crossStyles[props.theme]);

const crossClasss = computed(() => crossClasses[props.size]);

defineExpose({ badgeRef: spanRef });
</script>
